import { useState, useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';

import { useAuthStore } from '@/stores/useAuthStore';
import { paths } from '@/routes/paths';
import { useTcRunDetail } from '@/queries/useTcRun';
import { useTalentCommittees } from '@/queries/useTalentCommittees';
import { formatDate } from '@/utils/dateFormatter';
import type { CalibrationMatrixFilters, ConsultantGrade } from '@/api/types/calibrationMatrix.types';
import type { TcRunAppraiseeResponse } from '@/api/types/tcRun.types';
import { ANALYTICS_VALUE, GRADE_OPTIONS } from './calibration-matrix/lookups';
import { getGradeFamily } from './gradeUtils';
import { TcSelector } from './tc-run/TcSelector';
import { AppraiseeTable } from './tc-run/AppraiseeTable';
import { AppraiseeNavigationBar } from './tc-run/AppraiseeNavigationBar';
import { NotesToTcSection } from './tc-run/NotesToTcSection';
import { RatingsAndActionsSection } from './tc-run/RatingsAndActionsSection';
import { NotesForCollatorSection } from './tc-run/NotesForCollatorSection';
import { ConfidentialNotesSection } from './tc-run/ConfidentialNotesSection';
import { TcRunAppraiseeMutationQueueProvider } from './tc-run/TcRunAppraiseeMutationQueue';
import { computeSectionReadOnly } from './tc-run/sectionReadOnly';

function toMatrixGrade(grade: string | null | undefined): ConsultantGrade | null {
  const family = getGradeFamily(grade);
  return family && (GRADE_OPTIONS as string[]).includes(family)
    ? (family as ConsultantGrade)
    : null;
}

function buildPrefillFromAppraisee(
  appraisee: TcRunAppraiseeResponse,
  talentCommitteeId: number
): CalibrationMatrixFilters {
  const dept = (appraisee.department ?? '').trim();
  const isAnalytics = dept !== '' && dept.toLowerCase() === ANALYTICS_VALUE.toLowerCase();
  return {
    grade: toMatrixGrade(appraisee.billingGrade),
    analytics: dept === '' ? null : isAnalytics,
    geos: appraisee.geo ? [appraisee.geo] : [],
    sector: dept === '' ? null : isAnalytics ? ANALYTICS_VALUE : dept,
    talentCommitteeId,
  };
}

export function TalentCommitteeRunPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);
  const isHr = useAuthStore((state) => state.hasRole('HR'));
  const isStaffing = useAuthStore((state) => state.hasRole('Staffing'));
  const pageReadOnly = isHr && !isStaffing;

  // All hooks must be called before any conditional returns
  const [selectedTcId, setSelectedTcId] = useState<number | null>(null);
  const [currentAppraiseeIndex, setCurrentAppraiseeIndex] = useState<number>(0);

  const { data: tcRunDetail, isLoading: tcRunLoading } = useTcRunDetail(selectedTcId ?? 0);
  const { data: talentCommittees } = useTalentCommittees();
  const selectedTc = talentCommittees?.find((tc) => tc.id === selectedTcId) ?? null;

  const formattedTcDate = selectedTc?.plannedDate ? formatDate(selectedTc.plannedDate) : null;

  const appraisees = useMemo(() => tcRunDetail?.appraisees ?? [], [tcRunDetail]);
  const currentAppraisee = appraisees[currentAppraiseeIndex] ?? null;
  const selectedAppraisalId = currentAppraisee?.appraisalId ?? null;

  // Section editability: TC can edit only while it owns the appraisal
  // (TcReview / AwaitingHrGuidance / StaffingReview) and before Approve for Delivery.
  const sectionReadOnly = computeSectionReadOnly(currentAppraisee, pageReadOnly);

  const handleSelectTc = useCallback((tcId: number | null) => {
    setSelectedTcId(tcId);
    setCurrentAppraiseeIndex(0);
  }, []);

  const handleSelectAppraisee = useCallback(
    (appraisalId: number) => {
      const index = appraisees.findIndex((a) => a.appraisalId === appraisalId);
      if (index >= 0) {
        setCurrentAppraiseeIndex(index);
      }
    },
    [appraisees]
  );

  const handleSelectAppraiseeByIndex = useCallback((index: number) => {
    setCurrentAppraiseeIndex(index);
  }, []);

  // Redirect if user doesn't have required roles
  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mb: 3,
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Talent Committee Run
        </Typography>
        <TcSelector selectedTcId={selectedTcId} onSelect={handleSelectTc} />
      </Box>

      {selectedTcId && tcRunLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {tcRunDetail && selectedTc && (
        <Paper sx={{ mb: 3, borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'custom.darkTeal',
              color: 'common.white',
              p: 2,
            }}
          >
            <Typography component="h2" sx={{ typography: { xs: 'h6', md: 'h5' } }}>
              {selectedTc.name}
              {formattedTcDate && ` (${formattedTcDate})`}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <AppraiseeTable
              tcRunDetail={tcRunDetail}
              selectedAppraisalId={selectedAppraisalId}
              onSelectAppraisee={handleSelectAppraisee}
              readOnly={pageReadOnly}
            />
          </Box>
        </Paper>
      )}

      {tcRunDetail && (
        <>
          {currentAppraisee && (
            <>
              <AppraiseeNavigationBar
                appraiseeNames={appraisees.map((a) => `${a.firstName} ${a.lastName}`)}
                currentIndex={currentAppraiseeIndex}
                onSelect={handleSelectAppraiseeByIndex}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
                <Tooltip
                  title={
                    !currentAppraisee.billingGrade
                      ? 'Grade not set on this appraisee'
                      : !toMatrixGrade(currentAppraisee.billingGrade)
                        ? 'Grade not supported by Calibration Matrix'
                        : ''
                  }
                >
                  {/* span wrapper lets Tooltip attach to a disabled Button (MUI requirement). */}
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!toMatrixGrade(currentAppraisee.billingGrade)}
                      onClick={() => {
                        const prefill = buildPrefillFromAppraisee(
                          currentAppraisee,
                          tcRunDetail.talentCommitteeId
                        );
                        navigate(paths.staffingActivities.calibrationMatrix, {
                          state: { prefill },
                        });
                      }}
                    >
                      View in Calibration Matrix
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box key={currentAppraisee.appraisalId}>
                <TcRunAppraiseeMutationQueueProvider
                  tcId={tcRunDetail.talentCommitteeId}
                  appraisee={currentAppraisee}
                >
                  <NotesToTcSection appraisee={currentAppraisee} />

                  <RatingsAndActionsSection
                    tcId={tcRunDetail.talentCommitteeId}
                    appraisee={currentAppraisee}
                    readOnly={sectionReadOnly}
                  />

                  <NotesForCollatorSection
                    tcId={tcRunDetail.talentCommitteeId}
                    appraisee={currentAppraisee}
                    readOnly={sectionReadOnly}
                  />

                  <ConfidentialNotesSection
                    tcId={tcRunDetail.talentCommitteeId}
                    appraisee={currentAppraisee}
                    readOnly={sectionReadOnly}
                  />
                </TcRunAppraiseeMutationQueueProvider>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

export default TalentCommitteeRunPage;
